/* Dawn's slider handles touch, arrows, dots and slide visibility. This adds media lifecycle. */
if (!customElements.get('media-slideshow')) {
  class MediaSlideshow extends SlideshowComponent {
    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(slide => slide.clientWidth > 0);
      if (!this.sliderItemsToShow.length) return;
      this.sliderItemOffset = this.sliderItemsToShow.length > 1
        ? this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft
        : this.slider.clientWidth;
      this.slidesPerPage = 1;
      this.totalPages = this.sliderItemsToShow.length;
      this.update();
    }

    update() {
      if (!this.sliderItemsToShow?.length || !this.sliderItemOffset) return;
      const previousPage = this.currentPage;
      this.currentPage = Math.max(1, Math.min(
        this.sliderItemsToShow.length,
        Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1
      ));
      this.prevButton.disabled = this.sliderItemsToShow.length < 2;
      this.nextButton.disabled = this.sliderItemsToShow.length < 2;
      this.querySelectorAll('.slider-counter__link').forEach((link, index) => {
        const active = index === this.currentPage - 1;
        link.classList.toggle('slider-counter__link--active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.setSlideVisibility();
      if (previousPage !== this.currentPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] }
        }));
      }
    }

    setAutoPlay() {
      // Connect document/editor observers only while this section is mounted.
    }

    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.autoplaySpeed = Number(this.slider.dataset.speed) * 1000;
      this.userPaused = this.motion.matches;
      this.inView = false;
      this.editorPaused = false;
      this.initPages();
      this.sliderAutoplayButton.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      }, options);
      this.addEventListener('slideChanged', () => this.syncPlayback(), options);
      this.addEventListener('focusin', event => {
        this.focusPaused = event.target !== this.sliderAutoplayButton && !this.sliderAutoplayButton.contains(event.target);
        this.syncPlayback();
      }, options);
      this.addEventListener('focusout', event => {
        this.focusPaused = !!event.relatedTarget && this.contains(event.relatedTarget);
        this.syncPlayback();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.motion.addEventListener('change', () => {
        this.userPaused = this.motion.matches;
        this.syncPlayback();
      }, options);
      this.mobile.addEventListener('change', () => this.syncPlayback(), options);
      this.querySelectorAll('video').forEach(video => {
        video.addEventListener('ended', () => {
          if (this.dataset.advanceOnEnd === 'true' && !this.userPaused && !this.focusPaused && this.inView && !document.hidden && !this.editorPaused && this.slider.dataset.autoplay === 'true' && this.sliderItems[(this.currentPage || 1) - 1]?.contains(video)) {
            this.autoRotateSlides();
          }
        }, options);
        video.addEventListener('error', () => this.syncPlayback(), options);
      });
      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('.media-slideshow__slide');
        if (!slide) return;
        this.editorPaused = true;
        this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        this.syncPlayback();
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorPaused = false;
        this.syncPlayback();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.syncPlayback();
      }, { threshold: .1 });
      this.observer.observe(this);
      this.setSlideVisibility();
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.events || !this.sliderAutoplayButton) return;
      const running = !this.userPaused && this.inView && !document.hidden && !this.editorPaused;
      const active = this.sliderItems[(this.currentPage || 1) - 1];
      this.querySelectorAll('video').forEach(video => {
        const visible = active?.contains(video) && getComputedStyle(video).display !== 'none';
        if (running && visible) {
          window.PalmayardMedia?.prepare(video);
          video.muted = true;
          if (video.ended) video.currentTime = 0;
          video.play().catch(() => {
            // Keep the poster and resume image timing if the browser blocks playback.
            if (this.sliderItems[(this.currentPage || 1) - 1]?.contains(video) && !this.userPaused && !this.focusPaused && this.slider.dataset.autoplay === 'true') {
              clearInterval(this.autoplay);
              this.autoplay = setTimeout(() => this.autoRotateSlides(), this.autoplaySpeed);
            }
          });
        } else {
          video.pause();
        }
      });
      clearInterval(this.autoplay);
      const rotates = this.slider.dataset.autoplay === 'true' && this.sliderItems.length > 1;
      const activeVideo = [...(active?.querySelectorAll('video') || [])].find(video => getComputedStyle(video).display !== 'none');
      const waitingForVideo = this.dataset.advanceOnEnd === 'true' && activeVideo && !activeVideo.error;
      if (running && rotates && !this.focusPaused && !waitingForVideo) {
        this.autoplay = setInterval(() => this.autoRotateSlides(), this.autoplaySpeed);
      }
      this.slider.setAttribute('aria-live', running && rotates ? 'off' : 'polite');
      this.togglePlayButtonState(this.userPaused);
      this.sliderAutoplayButton.hidden = !rotates && !active?.querySelector('video');
    }

    setSlideVisibility(event) {
      super.setSlideVisibility(event);
      this.syncPlayback();
    }

    disconnectedCallback() {
      clearInterval(this.autoplay);
      clearTimeout(this.setPositionTimeout);
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  }
  customElements.define('media-slideshow', MediaSlideshow);
}
